import * as Tone from 'tone';
import { TEMPERAMENT_SYSTEMS, JI_SEMITONE_RATIOS } from '../data/musicData.js';


// Cache for loaded samplers
const samplerCache = new Map();
const activeSamplerUsers = new Map(); // Track which samplers are actively being used

// Use official Tone.js sample libraries
const INSTRUMENT_SAMPLES = {
  // Upright Piano — VSCO 2 Community Edition (CC0).
  // dyn2 (mezzo) round-robin 1, sampled every 4 semitones across the
  // typical comp register (F3–C#6). MIDI numbers match Tone's Yamaha
  // convention directly (middle C = C4 = MIDI 60), so url labels are
  // 1:1 with the underlying pitch.
  piano: {
    urls: {
      'F3':  'F3.mp3',
      'A3':  'A3.mp3',
      'C#4': 'Cs4.mp3',
      'F4':  'F4.mp3',
      'A4':  'A4.mp3',
      'C#5': 'Cs5.mp3',
      'F5':  'F5.mp3',
      'A5':  'A5.mp3',
      'C#6': 'Cs6.mp3',
    },
    baseUrl: '/audio/piano/'
  },
  casio: {
    urls: {
      A3: "A1.mp3",
      C3: "C2.mp3",
      "D#3": "Ds2.mp3",
      "F#3": "Fs2.mp3",
      A3: "A2.mp3",
    },
    baseUrl: 'https://tonejs.github.io/audio/casio/'
  },
  guitar: {
    urls: {
      'E2': 'E2.mp3', 'B2': 'B2.mp3', // 6th string: open + 7th fret
  'A2': 'A2.mp3', 'E3': 'E3.mp3', // 5th string: open + 7th fret  
  'D3': 'D3.mp3', 'A3': 'A3.mp3', // 4th string: open + 7th fret
  'G3': 'G3.mp3', // 3rd string: open + 7th fret
  'B3': 'B3.mp3', 'F#4': 'Fs4.mp3', // 2nd string: open + 7th fret
  'E4': 'E4.mp3', 'B4': 'B4.mp3' 
    },
    baseUrl: '/audio/guitar/'
  },
  trombone: {
    urls: {
      "Bb1": "bb1.mp3",
      "Bb2": "bb2.mp3",
      "Bb3": "bb3.mp3",
      "Bb4": "bb4.mp3",
    },
    baseUrl: '/audio/trombone/'
  },
  flute: {
    urls: {
      C4: "C4.mp3",
      C5: "C5.mp3",
      C6: "C6.mp3",
    },
    baseUrl: '/audio/flute/'
  },
  tanpura: {
    urls: {
      A2: "a-tanpura-thick.mp3",
      "Ab2": "ab-tanpura-thick.mp3",
      "Bb2": "bb-tanpura-thick.mp3",
      B2: "b-tanpura-thick.mp3",
      C3: "c-tanpura-thick.mp3",
      "Db2": "db-tanpura-thick.mp3",
      D2: "d-tanpura-thick.mp3",
      "Eb2": "eb-tanpura-thick.mp3",
      E2: "e-tanpura-thick.mp3",
      F2: "f-tanpura-thick.mp3",
      "Gb2": "gb-tanpura-thick.mp3",
      G2: "g-tanpura-thick.mp3",
    },
    baseUrl: '/audio/tanpuras/'
  },
  // Pizzicato upright contrabass — VSCO 2 Community Edition (CC0).
  // Single-velocity (v1 = soft fingertip pluck), one round-robin per
  // pitch, sampled every ~3 semitones across the contrabass register.
  //
  // IMPORTANT: VSCO uses Roland-style pitch notation (middle C = C3),
  // but Tone.Sampler expects Yamaha-style (middle C = C4). So a VSCO
  // file labeled "E0" is actually at MIDI 28 — Tone's "E1" — which is
  // the open low-E string of an upright bass (~41 Hz, physically
  // correct). The url MAP below uses the Tone-side label; the FILE
  // names on disk are the VSCO labels (one octave lower).
  // Upright bass — Logic Pro's "Club Bass Upright" patch. 10 pitches at
  // ~minor-third spacing across the bass range (E1–G3). Stereo 192k.
  // Logic uses Yamaha pitch notation so url labels match filenames 1:1
  // (no octave shift like the VSCO samples needed).
  bass: {
    urls: {
      'E1':  'E1.mp3',
      'G1':  'G1.mp3',
      'A#1': 'As1.mp3',
      'C2':  'C2.mp3',
      'E2':  'E2.mp3',
      'G2':  'G2.mp3',
      'A#2': 'As2.mp3',
      'C3':  'C3.mp3',
      'E3':  'E3.mp3',
      'G3':  'G3.mp3',
    },
    baseUrl: '/audio/bass/',
    // Logic Studio Bass at velocity 95 with normalize-on bounce is
    // already loud; a small attenuation puts it in the right place
    // against the SoCal kit.
    volume: -6,
  },
  // Retro vibe bells (FM bell-style synth) — Logic Pro patch, re-
  // bounced by the user with the pitch sequence corrected. 10 pitches
  // at PROPER minor-third spacing across the bass register (E1–G3):
  // E1 G1 A#1 C#2 E2 G2 A#2 C#3 E3 G3. The IDM aesthetic wants these
  // pitch-shifted UP at chord time for the smeared, wrong-octave bell
  // character.
  fmBells: {
    urls: {
      'E1':  'E1.mp3',
      'G1':  'G1.mp3',
      'A#1': 'As1.mp3',
      'C#2': 'Cs2.mp3',
      'E2':  'E2.mp3',
      'G2':  'G2.mp3',
      'A#2': 'As2.mp3',
      'C#3': 'Cs3.mp3',
      'E3':  'E3.mp3',
      'G3':  'G3.mp3',
    },
    baseUrl: '/audio/fmBells/',
    // FM bells have brilliant high harmonics that can pierce — sit
    // them well below piano level so they don't fight the rest of the
    // mix when used as comp.
    volume: -14,
  },
  // Modeled string pad — Logic Pro patch. Same 10-pitch low-register
  // bounce as fmBells, same caveats (top-note half-step error from the
  // original bounce). Sustained character; when used for chord stabs
  // at IDM tempos it pulses rather than washes (each note's natural
  // decay ends inside the bar).
  synthPad: {
    urls: {
      'E1':  'E1.mp3',
      'G1':  'G1.mp3',
      'A#1': 'As1.mp3',
      'C2':  'C2.mp3',
      'E2':  'E2.mp3',
      'G2':  'G2.mp3',
      'A#2': 'As2.mp3',
      'C3':  'C3.mp3',
      'E3':  'E3.mp3',
      'G3':  'G3.mp3',
    },
    baseUrl: '/audio/synthPad/',
    // Pads typically sit at moderate level — a bit higher than the
    // bells since the harmonic content is fuller / less piercing.
    volume: -10,
  },
  // Synth sub bass — Logic Pro patch, re-bounced by the user with the
  // pitch error corrected. 10 pitches at PROPER minor-third spacing
  // across the bass range (E1–G3): E1 G1 A#1 C#2 E2 G2 A#2 C#3 E3 G3.
  // Sub-heavy, short decay (~0.65 sec audible per note) — punchy IDM
  // feel rather than a sustained pad bass.
  synthBass: {
    urls: {
      'E1':  'E1.mp3',
      'G1':  'G1.mp3',
      'A#1': 'As1.mp3',
      'C#2': 'Cs2.mp3',
      'E2':  'E2.mp3',
      'G2':  'G2.mp3',
      'A#2': 'As2.mp3',
      'C#3': 'Cs3.mp3',
      'E3':  'E3.mp3',
      'G3':  'G3.mp3',
    },
    baseUrl: '/audio/synthBass/',
    // Synth sub is fundamental-heavy; sitting at -6 dB matches the
    // upright bass level so the variant swap doesn't change perceived
    // loudness for the rest of the mix.
    volume: -6,
  },
  // Electric bass — Karoryfer "fashionbass" (CC0). Killer KB-Simmony
  // fingered bass guitar, roundwounds, direct recording, mf velocity,
  // first round-robin. Sampled every ~3 semitones across the funk range.
  // Mono mp3 since bass is centered in the mix anyway.
  electricBass: {
    urls: {
      'F1':  'f1.mp3',
      'G#1': 'ab1.mp3',
      'B1':  'b1.mp3',
      'D2':  'd2.mp3',
      'F2':  'f2.mp3',
      'G#2': 'ab2.mp3',
      'B2':  'b2.mp3',
      'D3':  'd3.mp3',
      'F3':  'f3.mp3',
      'G#3': 'ab3.mp3',
    },
    baseUrl: '/audio/electricBass/',
    // Direct-recorded mf — louder out of the box than the VSCO upright.
    volume: -3,
  },
  // Rhodes Mark I — Logic Pro's "Suitcase MK1" patch. 9 pitches at
  // minor-third spacing across the comp register (F3–C#6), stereo
  // 192 kbps.
  rhodes: {
    urls: {
      'F3':  'F3.mp3',
      'A3':  'A3.mp3',
      'C#4': 'Cs4.mp3',
      'F4':  'F4.mp3',
      'A4':  'A4.mp3',
      'C#5': 'Cs5.mp3',
      'F5':  'F5.mp3',
      'A5':  'A5.mp3',
      'C#6': 'Cs6.mp3',
    },
    baseUrl: '/audio/rhodes/',
    // Logic bounces with Normalize ON — peaks near 0 dBFS. Bring down
    // a hair so chord stabs don't drown out bass + drums.
    volume: -10,
  },
  // Distorted electric bass — Logic Pro hi-gain DI bass patch. 10
  // pitches F1-G#3 (same coverage as electricBass / slapBass). Used by
  // the Painkiller-style metal bass variant — distortion saturation
  // delivers the chunky Ian Hill tone that clean electricBass can't.
  metalBass: {
    urls: {
      'F1':  'f1.mp3',
      'G#1': 'ab1.mp3',
      'B1':  'b1.mp3',
      'D2':  'd2.mp3',
      'F2':  'f2.mp3',
      'G#2': 'ab2.mp3',
      'B2':  'b2.mp3',
      'D3':  'd3.mp3',
      'F3':  'f3.mp3',
      'G#3': 'ab3.mp3',
    },
    baseUrl: '/audio/metalBass/',
    volume: -3,
  },
  // Distorted electric guitar — Logic Pro hi-gain power-chord patch.
  // 12 pitches F2-D5 at minor-third spacing — note this lives an
  // octave lower than wahGuitar/soulOrgan, because metal power chords
  // live in the F2-D4 register. The comp generator stacks root + 5th
  // programmatically to form power chords at the played pitches.
  metalGuitar: {
    urls: {
      'F2':  'f2.mp3',
      'G#2': 'ab2.mp3',
      'B2':  'b2.mp3',
      'D3':  'd3.mp3',
      'F3':  'f3.mp3',
      'G#3': 'ab3.mp3',
      'B3':  'b3.mp3',
      'D4':  'd4.mp3',
      'F4':  'f4.mp3',
      'G#4': 'ab4.mp3',
      'B4':  'b4.mp3',
      'D5':  'd5.mp3',
    },
    baseUrl: '/audio/metalGuitar/',
    volume: -8,
  },
  // Slap bass — Logic Pro 11 Studio Bass "Slap & Pop" patch, Slap
  // articulation. Same 10-pitch coverage as electricBass (F1-G#3).
  // Slap notes decay much faster than fingered electric bass, so the
  // bounced samples are short (~1.5s vs electricBass's ~6s) — that
  // matches the technique. Wired in via the 'slap' bass variant in
  // applyVariants, which swaps both generator (funkSlapBass) and
  // instrument (slapBass) when selected on a Funk chart.
  slapBass: {
    urls: {
      'F1':  'f1.mp3',
      'G#1': 'ab1.mp3',
      'B1':  'b1.mp3',
      'D2':  'd2.mp3',
      'F2':  'f2.mp3',
      'G#2': 'ab2.mp3',
      'B2':  'b2.mp3',
      'D3':  'd3.mp3',
      'F3':  'f3.mp3',
      'G#3': 'ab3.mp3',
    },
    baseUrl: '/audio/slapBass/',
    volume: -3,
  },
  // Wah-wah guitar — Logic Pro electric guitar with auto-wah / pedal-
  // wah envelope. 12 pitches at minor-third spacing F3–D6. The filter
  // resonance gives notes a continuous expressive character — bounced
  // samples capture both the picked attack and the swept tail. Wired
  // via the 'wahwah' comp variant on Funk charts; the generator
  // (wahGuitarComping) plays single chord tones, not chord stacks, so
  // the wah character reads as a melodic line.
  wahGuitar: {
    urls: {
      'F3':  'f3.mp3',
      'G#3': 'ab3.mp3',
      'B3':  'b3.mp3',
      'D4':  'd4.mp3',
      'F4':  'f4.mp3',
      'G#4': 'ab4.mp3',
      'B4':  'b4.mp3',
      'D5':  'd5.mp3',
      'F5':  'f5.mp3',
      'G#5': 'ab5.mp3',
      'B5':  'b5.mp3',
      'D6':  'd6.mp3',
    },
    baseUrl: '/audio/wahGuitar/',
    volume: -8,
  },
  // Soul organ — Logic Pro Hammond B3-style patch. Same 12-pitch
  // F3–D6 coverage. Tonewheel sustain + percussion attack — fills
  // the harmonic space without the percussive transients of clavinet
  // or the bright fingertip attack of rhodes. Wired via the 'organ'
  // comp variant on Funk charts.
  soulOrgan: {
    urls: {
      'F3':  'f3.mp3',
      'G#3': 'ab3.mp3',
      'B3':  'b3.mp3',
      'D4':  'd4.mp3',
      'F4':  'f4.mp3',
      'G#4': 'ab4.mp3',
      'B4':  'b4.mp3',
      'D5':  'd5.mp3',
      'F5':  'f5.mp3',
      'G#5': 'ab5.mp3',
      'B5':  'b5.mp3',
      'D6':  'd6.mp3',
    },
    baseUrl: '/audio/soulOrgan/',
    volume: -10,
  },
  // Clavinet — Logic Pro's "Clavinet" patch. Same pitch coverage as
  // rhodes/piano. Percussive Hohner D6 sound — Stevie Wonder territory.
  // Useful for funk-rock variations or as an extra Funk style option.
  clavinet: {
    urls: {
      'F3':  'F3.mp3',
      'A3':  'A3.mp3',
      'C#4': 'Cs4.mp3',
      'F4':  'F4.mp3',
      'A4':  'A4.mp3',
      'C#5': 'Cs5.mp3',
      'F5':  'F5.mp3',
      'A5':  'A5.mp3',
      'C#6': 'Cs6.mp3',
    },
    baseUrl: '/audio/clavinet/',
    volume: -10,
  }
};

// Function to calculate temperament-adjusted frequency
function calculateTemperamentFrequency(midiNote, temperamentSystem, referencePitch, aTuning = 440) {
  if (temperamentSystem === 'Equal Temperament' || !temperamentSystem) {
    return aTuning * Math.pow(2, (midiNote - 69) / 12);
  } else if (temperamentSystem === 'Just Intonation') {
    const semitone = (midiNote - 12) % 12;
    const octave = Math.floor((midiNote - 12) / 12);
    
    if (semitone === referencePitch) {
      // This IS the reference note, use equal temperament
      return aTuning * Math.pow(2, (midiNote - 69) / 12);
    }
    
    const interval = (semitone - referencePitch + 12) % 12;
    const jiRatio = JI_SEMITONE_RATIOS[interval];
    
    if (jiRatio) {
      // Calculate the frequency of the reference note in the same octave
      const referenceNoteMidiNumber = (octave * 12 + referencePitch) + 12;
      const referenceFreq = aTuning * Math.pow(2, (referenceNoteMidiNumber - 69) / 12);
      return referenceFreq * (jiRatio.numerator / jiRatio.denominator);
    } else {
      // Fallback to equal temperament
      return aTuning * Math.pow(2, (midiNote - 69) / 12);
    }
  } else {
    // Historical temperaments
    const semitone = (midiNote - 12) % 12;
    const temperament = TEMPERAMENT_SYSTEMS[temperamentSystem];
    const etFreq = aTuning * Math.pow(2, (midiNote - 69) / 12);
    const offset = temperament.offsets[semitone];
    return etFreq * Math.pow(2, offset / 1200);
  }
}

// Enhanced function to play samples with temperament support
export function playSampleWithTemperament(sampler, midiNote, duration, startTime, velocity, temperamentSystem, referencePitch, aTuning = 440) {
  if (!sampler) return;

  try {
    // Calculate temperament-adjusted frequency
    const temperamentFreq = calculateTemperamentFrequency(midiNote, temperamentSystem, referencePitch, aTuning);
    
    // Convert temperament frequency back to MIDI note for triggering
    const temperamentMidi = 69 + 12 * Math.log2(temperamentFreq / 440);
    const adjustedNoteName = Tone.Frequency(temperamentMidi, 'midi').toNote();
    
    const compensatedStartTime = startTime - 0.015;
    
    // Use the temperament-adjusted note name instead of detune
    if (duration) {
      if (duration < 0.2) {
        const adjustedDuration = Math.min(duration * 0.8, 0.15);
        sampler.triggerAttackRelease(adjustedNoteName, adjustedDuration, compensatedStartTime, velocity);
      } else {
        sampler.triggerAttackRelease(adjustedNoteName, duration, compensatedStartTime, velocity);
      }
    } else {
      sampler.triggerAttack(adjustedNoteName, compensatedStartTime, velocity);
    }
    
  } catch (error) {
    console.warn(`Error playing sample with temperament:`, error);
    // Fallback to original note if temperament calculation fails
    try {
      const fallbackNoteName = Tone.Frequency(midiNote, 'midi').toNote();
      if (duration) {
        sampler.triggerAttackRelease(fallbackNoteName, duration, startTime, velocity);
      } else {
        sampler.triggerAttack(fallbackNoteName, startTime, velocity);
      }
    } catch (fallbackError) {
      console.warn(`Fallback also failed:`, fallbackError);
    }
  }
}

export async function getSampler(instrument, masterGainNode) {
  // Return cached sampler if available
  if (samplerCache.has(instrument)) {
    return samplerCache.get(instrument);
  }

  const sampleConfig = INSTRUMENT_SAMPLES[instrument];
  if (!sampleConfig) {
    console.error('No samples found for instrument:', instrument);
    return null;
  }

  try {
    // Ensure Tone.js context is started
    if (Tone.context.state !== 'running') {
      await Tone.start();
      console.log('Tone.js context started');
    }

    // Create sampler using the official Tone.js pattern
    const sampler = await new Promise((resolve, reject) => {
      const samplerInstance = new Tone.Sampler({
        urls: sampleConfig.urls,
        baseUrl: sampleConfig.baseUrl,
        onload: () => {
          console.log(`${instrument} samples loaded successfully`);
          resolve(samplerInstance);
        },
        onerror: (error) => {
          console.error(`Error loading ${instrument} samples:`, error);
          reject(error);
        }
      });

      // Set a reasonable timeout
      setTimeout(() => {
        reject(new Error(`Timeout loading ${instrument} samples after 15 seconds`));
      }, 15000);
    });

    // Connect to destination using Tone.js's built-in routing
    sampler.toDestination();

    // Per-instrument volume override (dB) from INSTRUMENT_SAMPLES; default
    // -12 dB to prevent clipping for general-purpose samples. Used for
    // bass, where VSCO's v1 (soft fingertip pluck) layer is intentionally
    // quiet and needs +14 dB to sit at the level of drums and comp.
    sampler.volume.value =
      typeof sampleConfig.volume === 'number' ? sampleConfig.volume : -12;
    
    // Cache the sampler for reuse
    samplerCache.set(instrument, sampler);
    
    return sampler;
    
  } catch (err) {
    console.error(`Failed to create ${instrument} sampler:`, err);
    return null;
  }
}

// New function to mark a sampler as in use
export function markSamplerInUse(instrument, userId) {
  if (!activeSamplerUsers.has(instrument)) {
    activeSamplerUsers.set(instrument, new Set());
  }
  activeSamplerUsers.get(instrument).add(userId);
  console.log(`🎯 Marked ${instrument} sampler in use by ${userId}`);
}

// New function to mark a sampler as no longer in use
export function markSamplerNotInUse(instrument, userId) {
  if (activeSamplerUsers.has(instrument)) {
    activeSamplerUsers.get(instrument).delete(userId);
    if (activeSamplerUsers.get(instrument).size === 0) {
      activeSamplerUsers.delete(instrument);
    }
    console.log(`🎯 Marked ${instrument} sampler no longer in use by ${userId}`);
  }
}

// Updated cleanup function to only dispose unused samplers
export function disposeSamplers() {
  console.log(`🗑️ disposeSamplers called from:`, new Error().stack);
  console.log(`🗑️ Active sampler users:`, Array.from(activeSamplerUsers.keys()));
  
  samplerCache.forEach((sampler, instrument) => {
    const isInUse = activeSamplerUsers.has(instrument) && activeSamplerUsers.get(instrument).size > 0;
    
    if (!isInUse) {
      try {
        sampler.dispose();
        console.log(`${instrument} sampler disposed (not in use)`);
      } catch (err) {
        console.warn(`Error disposing ${instrument} sampler:`, err);
      }
    } else {
      console.log(`${instrument} sampler kept alive (in use by: ${Array.from(activeSamplerUsers.get(instrument))})`);
    }
  });
  
  // Only clear cache for disposed samplers
  samplerCache.forEach((sampler, instrument) => {
    const isInUse = activeSamplerUsers.has(instrument) && activeSamplerUsers.get(instrument).size > 0;
    if (!isInUse) {
      samplerCache.delete(instrument);
    }
  });
}

// Function to calculate available MIDI notes for an instrument
export function getAvailableNotesForInstrument(instrument) {
  const sampleConfig = INSTRUMENT_SAMPLES[instrument];
  if (!sampleConfig) {
    return [];
  }

  const availableNotes = new Set();
  const transpositionRange = 12; // +/- 1 octave is reasonable for most samples

  // Convert note names to MIDI numbers and calculate transposition range
  Object.keys(sampleConfig.urls).forEach(noteName => {
    try {
      // Convert Tone.js note name to MIDI number
      const midiNote = Tone.Frequency(noteName).toMidi();
      
      // Add the base note and its transposition range
      for (let offset = -transpositionRange; offset <= transpositionRange; offset++) {
        const transposedNote = midiNote + offset;
        // Keep within reasonable MIDI range (C1 to C8)
        if (transposedNote >= 24 && transposedNote <= 108) {
          availableNotes.add(transposedNote);
        }
      }
    } catch (error) {
      console.warn(`Could not parse note name: ${noteName}`, error);
    }
  });

  return Array.from(availableNotes).sort((a, b) => a - b);
}

// Function to get the octave range for an instrument (for UI purposes)
export function getOctaveRangeForInstrument(instrument) {
  const availableNotes = getAvailableNotesForInstrument(instrument);
  if (availableNotes.length === 0) {
    return { min: 3, max: 6 }; // Default range
  }

  const minOctave = Math.floor((Math.min(...availableNotes) - 12) / 12);
  const maxOctave = Math.floor((Math.max(...availableNotes) - 12) / 12);
  
  return { min: Math.max(1, minOctave), max: Math.min(8, maxOctave) };
}

// Get all available instruments
export function getAvailableInstruments() {
  return Object.keys(INSTRUMENT_SAMPLES);
}

// Export instrument samples for UI purposes
export { INSTRUMENT_SAMPLES };

